// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState, useCallback, useMemo } from "react";

// @mui material components
import {
  Card,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Box,
  Checkbox,
  Chip,
  MenuItem,
  Modal,
  Select,
  Tabs,
  Tab,
  Tooltip,
  Autocomplete,
  TextField,
} from "@mui/material";

import { gql, useMutation, useQuery } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import {
  parseSeccionesComanda,
  serializeSeccionesComanda,
  SECCIONES_COMANDA,
} from "utils/seccionesComanda";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "95%",
  width: { xs: "60%" },
  overflowY: "auto",
  p: 3,
};

const productoInicial = {
  nombre: "",
  idCategoria: "",
  precioCarta: "",
  precioOnline: "",
  descripcion: "",
  seccion: ["Cocina"],
  cantidadPorMayor: 0,
};

export default function ModalProductoComercial({
  open,
  handleClose,
  refetch,
  categorias,
  selectedItem = null
}) {
  const [producto, setProducto] = useState(productoInicial);
  const { handleSnackbar } = useContext(MessageManager);
  const [materiaPrima, setMateriaPrima] = useState({
    idMateriaPrima: "",
    idTipoUnidad: "",
  });
  const [camposConError, setCamposConError] = useState(new Set());
  const [mostrarTooltips, setMostrarTooltips] = useState(false);

  const [saveProductoComercial, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveProductoComercial($input: ProductoInput!) {
        saveProductoComercial(input: $input) {
          id
        }
      }
    `
  );

  const { data: dataCategorias } = useQuery(
    gql`
      query categoriasProducto {
        categoriasProducto {
          id
          categoria
        }
      }
    `,
    {
      fetchPolicy: "network-only",
      skip: !open || categorias.length > 0,
    }
  );

  const categoriasOptions = categorias && categorias.length > 0
    ? categorias
    : dataCategorias?.categoriasProducto || [];

  const { data: materiaPrimaData, refetch: refetchMateriaPrima, loading } = useQuery(
    gql`
    query materiasPrimas {
      materiasPrimas(
        filter: { tipo: ["Consumible", "Plato_Ingrediente"], categoria: { _ne: "Descartables"}, disabled: false, _stock: { idSucursal: 1, cantidad: {_ne: null} } }
      ) {
        id
        nombre
        precio
        porcentajeUtilizable
        tipo
        categoria
        disabled
        precio
        stock(idSucursal: 1) {
          cantidad
        }
        tipoUnidad {
          id
          nombre
          abreviatura
          multiplicador
        }
      }
    }
  `,
    {
      skip: !open || selectedItem?.id,
      fetchPolicy: "network-only",
    }
  );

  useEffect(() => {
    if (open) {
      setProducto(productoInicial);
      setCamposConError(new Set());
      setMostrarTooltips(false);
      if (selectedItem) {
        setMateriaPrima({
          idMateriaPrima: selectedItem?.id,
          idTipoUnidad: selectedItem?.tipoUnidad?.id,
          nombre: selectedItem?.nombre,
          unidadOriginal: selectedItem?.tipoUnidad,
          stock: selectedItem?.stock,
          disabled: selectedItem?.disabled,
          precio: selectedItem?.precio
        });
        setProducto({
          ...productoInicial,
          precioCarta: selectedItem?.precio || 0,
        });
      } else {
        setMateriaPrima({
          idMateriaPrima: "",
          idTipoUnidad: "",
        });
      }
    }
  }, [open]);

  const handleChangeProducto = (e) => {
    const { name, value } = e.target;
    setProducto({
      ...producto,
      [name]:
        name === "seccion"
          ? parseSeccionesComanda(typeof value === "string" ? value.split(",") : value)
          : value,
    });
  };

  const isPrecioValido = useCallback((valor) => {
    return (
      valor !== null &&
      valor !== undefined &&
      valor !== "" &&
      !isNaN(Number(valor)) &&
      Number(valor) > 0
    );
  }, []);

  // Función para crear tooltip con error
  const crearTooltipError = useCallback((campo, children, mensajeError) => {
    if (!open) return children;

    const tieneError = camposConError.has(campo) && mostrarTooltips;

    if (tieneError) {
      return (
        <Tooltip
          title={mensajeError || "Este campo es obligatorio"}
          placement="top"
          open={true}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'error.main',
                '& .MuiTooltip-arrow': {
                  color: 'error.main',
                },
              },
            },
          }}
        >
          {children}
        </Tooltip>
      );
    }

    return children;
  }, [open, camposConError, mostrarTooltips]);

  const preciosPorMayorHabilitados = useMemo(() => {
    return producto.cantidadPorMayor > 0;
  }, [producto.cantidadPorMayor]);

  // Limpiar precios por mayor cuando cantidadPorMayor es 0
  useEffect(() => {
    if (producto.cantidadPorMayor == 0) {
      setProducto(prev => ({
        ...prev,
        precioPorMayor: "",
        precioPorMayorOnline: "",
      }));
    }
  }, [producto.cantidadPorMayor]);

  const handleSave = () => {
    // Validar campos obligatorios y mostrar tooltips si hay errores
    const errores = new Set();

    // Validaciones básicas
    if (!materiaPrima.idMateriaPrima) {
      errores.add('materiaPrima');
    }
    if (!producto.idCategoria) {
      errores.add('idCategoria');
    }
    if (parseSeccionesComanda(producto.seccion).length === 0) {
      errores.add('seccion');
    }

    // Validaciones de precios
    if (!isPrecioValido(producto.precioCarta)) {
      errores.add('precioCarta');
    }
    if (!isPrecioValido(producto.precioOnline)) {
      errores.add('precioOnline');
    }

    // Solo validar precios por mayor si cantidadPorMayor > 0
    if (producto.cantidadPorMayor > 0) {
      if (!isPrecioValido(producto.precioPorMayor)) {
        errores.add('precioPorMayor');
      }
      if (!isPrecioValido(producto.precioPorMayorOnline)) {
        errores.add('precioPorMayorOnline');
      }
    }

    // Validación de precio vs costo (el precio debería ser mayor al costo de la materia prima)
    if (materiaPrima.precio && producto.precioCarta) {
      if (parseFloat(materiaPrima.precio) >= parseFloat(producto.precioCarta)) {
        errores.add('precioMenorCosto');
        errores.add('precioCarta'); // También marcar el campo precio como error
      }
    }

    // Si hay errores, mostrar tooltips y no guardar
    if (errores.size > 0) {
      setCamposConError(errores);
      setMostrarTooltips(true);

      // Ocultar tooltips después de 5 segundos
      setTimeout(() => {
        setMostrarTooltips(false);
        setCamposConError(new Set());
      }, 5000);

      // Mostrar mensaje de error específico
      let mensajeError = "Por favor complete todos los campos obligatorios";
      if (errores.has('precioMenorCosto')) {
        mensajeError = `El precio de venta ($${producto.precioCarta}) debe ser mayor al costo de la materia prima ($${materiaPrima.precio})`;
      }
      handleSnackbar(mensajeError, "error");
      return;
    }

    // Si no hay errores, proceder con el guardado
    saveProductoComercial({
      variables: {
        input: {
          id: null,
          nombre: materiaPrima.nombre,
          idCategoria: producto.idCategoria,
          descripcion: producto.descripcion || "",
          precioCarta: producto.precioCarta || null,
          precioOnline: producto.precioOnline || null,
          precioPorMayor: producto.precioPorMayor || null,
          precioPorMayorOnline: producto.precioPorMayorOnline || null,
          cantidadPorciones: 1,
          cantidadPorMayor: Number(producto.cantidadPorMayor) || null,
          seccion: serializeSeccionesComanda(producto.seccion),
          comercial: true,
          disponibilidad: true,
          idMateriaPrima: materiaPrima.idMateriaPrima,
          idTipoUnidad: materiaPrima.idTipoUnidad,
          esElaborado: false,
          esIngrediente: false,
          combo: false,
          disponibilidad: true,
          precioCosto: materiaPrima.precio || 0,
        },
      },
    })
      .then(() => {
        handleSnackbar("Producto comercial guardado correctamente", "success");
        refetch();
        handleClose();
      })
      .catch((error) => {
        const errorMessage = error?.graphQLErrors?.[0]?.message || "Atención: ";
        handleSnackbar(errorMessage, "warning");
      });
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <SoftTypography variant="h6">Producto comercial</SoftTypography>
              </SoftBox>
              <SoftBox display="flex" justifyContent="space-between">
                <SoftBox>
                  <Tooltip title="Cerrar" placement="top">
                    <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                      close
                    </Icon>
                  </Tooltip>
                </SoftBox>
              </SoftBox>
            </SoftBox>
            <SoftBox py={2}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  {crearTooltipError(
                    'materiaPrima',
                    <InputLabel htmlFor="nombre">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Seleccione un elemento del inventario para vender
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>,
                    "Debe seleccionar una materia prima"
                  )}
                  {materiaPrima.idMateriaPrima ? (
                    <SoftBox display="flex" alignItems="center" justifyContent="space-between">
                      <SoftTypography variant="h6" fontWeight="bold">
                        <SoftBox display="flex" alignItems="center">
                          {(() => {
                            const selectedMateriaPrima = selectedItem?.id ? selectedItem : materiaPrima.idMateriaPrima ? (materiaPrimaData?.materiasPrimas || []).find(mp => mp.id === materiaPrima.idMateriaPrima) || null : null
                            return selectedMateriaPrima
                              && (
                                <>
                                  {`${selectedMateriaPrima?.id} - ${selectedMateriaPrima?.nombre} `}
                                  <SoftTypography variant="caption" color="secondary">
                                    &nbsp;&nbsp;{`(Stock: ${selectedMateriaPrima?.stock?.cantidad} ${selectedMateriaPrima?.tipoUnidad?.abreviatura})`}
                                  </SoftTypography>
                                </>
                              )
                          })()}
                        </SoftBox>
                      </SoftTypography>
                      {materiaPrima.idMateriaPrima && !selectedItem?.id && <Tooltip placement="top" title="Cambiar producto">
                        <SoftButton
                          color="primary"
                          circular
                          iconOnly
                          onClick={() => {
                            setMateriaPrima({
                              idMateriaPrima: "",
                              idTipoUnidad: "",
                              cantidad: "",
                            });
                            setProducto(productoInicial);
                          }}
                          sx={{ ml: 2 }}
                        >
                          <Icon>close</Icon>
                        </SoftButton>
                      </Tooltip>}
                    </SoftBox>) :
                    (<Autocomplete
                      options={materiaPrimaData?.materiasPrimas || []}
                      getOptionLabel={(option) => option.nombre}
                      loading={loading}
                      value={
                        materiaPrima.idMateriaPrima
                          ? (materiaPrimaData?.materiasPrimas || []).find(mp => mp.id === materiaPrima.idMateriaPrima) || null
                          : null
                      }
                      onChange={(e, newValue) => {
                        if (!newValue || newValue?.stock?.cantidad <= 0) return
                        if (newValue) {
                          setMateriaPrima({
                            ...materiaPrima,
                            ...newValue,
                            idMateriaPrima: newValue?.id || "",
                            idTipoUnidad: newValue?.tipoUnidad?.id || "",
                            unidadOriginal: newValue?.tipoUnidad,
                          });
                          setProducto({
                            ...producto,
                            precioCarta: newValue?.precio || 0,
                          });
                        } else {
                          setMateriaPrima({
                            idMateriaPrima: "",
                          });
                          setProducto({
                            ...producto,
                            precioCarta: null,
                          });
                        }
                      }}
                      getOptionDisabled={(option) => !option.stock || option.stock.cantidad <= 0}
                      renderOption={(props, option) => {
                        return (
                          <li {...props}>
                            {option.nombre} - &nbsp;<strong>{`Stock: ${option.stock?.cantidad} ${option?.tipoUnidad?.nombre}`}</strong>
                            {(!option.stock || option.stock.cantidad <= 0) && <span style={{ color: 'red', marginLeft: 8 }}>(Sin stock)</span>}
                          </li>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          InputProps={{ ...params.InputProps }}
                        />
                      )}
                    />)
                  }
                </Grid>
                <Grid item xs={6}>
                  {crearTooltipError(
                    'seccion',
                    <InputLabel htmlFor="nombre">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Sección comandera
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                  )}
                  {crearTooltipError(
                    'seccion',
                    <Select
                      placeholder="Seleccione sección comandera"
                      multiple
                      value={producto.seccion || []}
                      onChange={handleChangeProducto}
                      name="seccion"
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((seccion, index) => (
                            <Chip key={seccion + index} label={seccion} />
                          ))}
                        </Box>
                      )}
                    >
                      {SECCIONES_COMANDA.map((seccion) => (
                        <MenuItem key={seccion} value={seccion}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Checkbox
                              checked={(producto.seccion || []).indexOf(seccion) > -1}
                              sx={{ mr: 1, p: 0 }}
                            />
                            {seccion}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <InputLabel htmlFor="categoria">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Categoría
                      <SoftTypography variant="caption" fontWeight="bold" color="primary">
                        &nbsp;*
                      </SoftTypography>
                    </SoftTypography>
                  </InputLabel>
                  {crearTooltipError(
                    'idCategoria',
                    <Select
                      placeholder="Seleccione una categoría"
                      value={producto.idCategoria}
                      onChange={handleChangeProducto}
                      name="idCategoria"
                    >
                      {(categoriasOptions || []).map((categoria) => (
                        <MenuItem key={categoria.id} value={categoria.id}>
                          {categoria.categoria}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Grid>
                <Grid item xs={4} sm={3}>
                  {crearTooltipError(
                    'precioCarta',
                    <InputLabel htmlFor="precioCarta">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Precio por porción
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>,
                    camposConError.has('precioMenorCosto') ? "El precio debe ser mayor al costo de la materia prima" : "Este campo es obligatorio y debe ser mayor a 0"
                  )}
                  {crearTooltipError(
                    'precioCarta',
                    <SoftInputNumber
                      label="precioCarta"
                      placeholder="Ingrese el precio"
                      icon={{
                        component: <Icon color="primary">attach_money</Icon>,
                        direction: "left",
                      }}
                      name="precioCarta"
                      value={producto.precioCarta}
                      onChange={handleChangeProducto}
                    />,
                    camposConError.has('precioMenorCosto') ? "El precio debe ser mayor al costo de la materia prima" : "Este campo es obligatorio y debe ser mayor a 0"
                  )}
                </Grid>
                <Grid item xs={4} sm={3}>
                  {crearTooltipError(
                    'precioOnline',
                    <InputLabel htmlFor="precioOnline">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Precio por porción para llevar
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                  )}
                  {crearTooltipError(
                    'precioOnline',
                    <SoftInputNumber
                      label="precioOnline"
                      placeholder="Ingrese el precio"
                      icon={{
                        component: <Icon color="primary">attach_money</Icon>,
                        direction: "left",
                      }}
                      name="precioOnline"
                      value={producto.precioOnline}
                      onChange={handleChangeProducto}
                    />
                  )}
                </Grid>
                <Grid item xs={3} sm={3}>
                  <InputLabel htmlFor="cantidadPorMayor">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Mínimo para precio por mayor
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="cantidadPorMayor"
                    placeholder="Ingrese la cantidad"
                    type="number"
                    name="cantidadPorMayor"
                    value={producto.cantidadPorMayor}
                    onChange={handleChangeProducto}
                  />
                </Grid>
                <Grid item xs={4} sm={3}>
                  {preciosPorMayorHabilitados ? crearTooltipError(
                    'precioPorMayor',
                    <InputLabel htmlFor="precioPorMayor">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Precio por Mayor
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                  ) : (
                    <InputLabel htmlFor="precioPorMayor">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Precio por Mayor
                        {preciosPorMayorHabilitados && (
                          <SoftTypography variant="caption" fontWeight="bold" color="primary">
                            &nbsp;*
                          </SoftTypography>
                        )}
                      </SoftTypography>
                    </InputLabel>
                  )}
                  {preciosPorMayorHabilitados ? crearTooltipError(
                    'precioPorMayor',
                    <SoftInputNumber
                      label="precioPorMayor"
                      placeholder="Ingrese el precio"
                      icon={{
                        component: <Icon color="primary">attach_money</Icon>,
                        direction: "left",
                      }}
                      name="precioPorMayor"
                      value={producto.precioPorMayor}
                      onChange={handleChangeProducto}
                      disabled={!preciosPorMayorHabilitados}
                    />
                  ) : (
                    <SoftInputNumber
                      label="precioPorMayor"
                      placeholder="Ingrese el precio"
                      icon={{
                        component: <Icon color="primary">attach_money</Icon>,
                        direction: "left",
                      }}
                      name="precioPorMayor"
                      value={producto.precioPorMayor}
                      onChange={handleChangeProducto}
                      disabled={!preciosPorMayorHabilitados}
                    />
                  )}
                </Grid>
                <Grid item xs={4} sm={3}>
                  {preciosPorMayorHabilitados ? crearTooltipError(
                    'precioPorMayorOnline',
                    <InputLabel htmlFor="precioPorMayorOnline">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Precio por Mayor para llevar
                        <SoftTypography variant="caption" fontWeight="bold" color="primary">
                          &nbsp;*
                        </SoftTypography>
                      </SoftTypography>
                    </InputLabel>
                  ) : (
                    <InputLabel htmlFor="precioPorMayorOnline">
                      <SoftTypography component="label" variant="caption" fontWeight="bold">
                        Precio por Mayor para llevar
                        {preciosPorMayorHabilitados && (
                          <SoftTypography variant="caption" fontWeight="bold" color="primary">
                            &nbsp;*
                          </SoftTypography>
                        )}
                      </SoftTypography>
                    </InputLabel>
                  )}
                  {preciosPorMayorHabilitados ? crearTooltipError(
                    'precioPorMayorOnline',
                    <SoftInputNumber
                      label="precioPorMayorOnline"
                      placeholder="Ingrese el precio"
                      icon={{
                        component: <Icon color="primary">attach_money</Icon>,
                        direction: "left",
                      }}
                      name="precioPorMayorOnline"
                      value={producto.precioPorMayorOnline}
                      onChange={handleChangeProducto}
                      disabled={!preciosPorMayorHabilitados}
                    />
                  ) : (
                    <SoftInputNumber
                      label="precioPorMayorOnline"
                      placeholder="Ingrese el precio"
                      icon={{
                        component: <Icon color="primary">attach_money</Icon>,
                        direction: "left",
                      }}
                      name="precioPorMayorOnline"
                      value={producto.precioPorMayorOnline}
                      onChange={handleChangeProducto}
                      disabled={!preciosPorMayorHabilitados}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <InputLabel htmlFor="descripcion">
                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                      Descripción
                    </SoftTypography>
                  </InputLabel>
                  <SoftInput
                    label="descripcion"
                    placeholder="Descripción del producto"
                    type="text"
                    name="descripcion"
                    multiline
                    rows={2}
                    value={producto.descripcion}
                    onChange={handleChangeProducto}
                  />
                </Grid>
              </Grid>
            </SoftBox>
            <SoftBox
              display={{
                xs: "flex-row",
                sm: "flex",
              }}
              justifyContent="end"
              alignItems="center"
            >
              <SoftBox
                display={{
                  xs: "flex-row",
                  sm: "flex",
                }}
                justifyContent="end"
                alignItems="center"
              >
                <SoftBox
                  mr={{
                    xs: 0,
                    sm: 2,
                  }}
                  mt={{
                    xs: 2,
                    sm: 0,
                  }}
                >
                  <SoftButton
                    color="primary"
                    circular
                    onClick={handleClose}
                    fullWidth={{
                      xs: true,
                      sm: false,
                    }}
                    disabled={loadingSave}
                  >
                    <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                    &nbsp;Cancelar
                  </SoftButton>
                </SoftBox>
                <SoftBox
                  mt={{
                    xs: 2,
                    sm: 0,
                  }}
                >
                  <SoftButton
                    color="uproGreen"
                    circular
                    fullWidth={{
                      xs: true,
                      sm: false,
                    }}
                    onClick={handleSave}
                    disabled={loadingSave}
                  >
                    <Icon sx={{ fontWeight: "light" }}>save</Icon>
                    &nbsp;Guardar
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalProductoComercial.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetch: PropTypes.func,
  categorias: PropTypes.array,
};
